import { ChevronRight, Library } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import type { LibraryCitation } from '@/api/services/book-ai-purpose'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { readerPositionPath } from '@/routes/paths'
import { type LibraryChatMessage, useLibraryChatStore } from '@/store/useLibraryChatStore'

import { AssistantBubble, SuggestionChips, ThinkingIndicator, UserBubble } from './chat-parts'
import { CitationChip } from './citation-chip'
import { citationLabel } from './citation-label'

type Props = {
  /** 今開いている本（この本の本文なら、開き直さずにその位置へ移動する） */
  episodeId: number
  onNavigate: (key: number) => void
}

const INTRO =
  'AI 解析済みのすべての本から探して、どの本に書いてあるかを示して答えます。本棚全体から探すので、ネタバレ防止は効きません。たとえば:'

const SUGGESTIONS = ['複数の本で共通して言っていることは？', '本によって意見が分かれている点は？']

/** 本棚全体への質問の履歴（根拠は本の名前付きで出し、押すとその本のその位置を開く） */
export const LibraryChat = ({ episodeId, onNavigate }: Props) => {
  const messages = useLibraryChatStore((state) => state.messages)
  const isSending = useLibraryChatStore((state) => state.isSending)
  const send = useLibraryChatStore((state) => state.send)
  const navigate = useNavigate()
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, isSending])

  const open = (citation: LibraryCitation) => {
    if (citation.episode_id === episodeId) onNavigate(citation.key)
    else navigate(readerPositionPath(citation.episode_id, citation.key))
  }

  return (
    <div className="space-y-3">
      {messages.length === 0 && !isSending && <SuggestionChips intro={INTRO} suggestions={SUGGESTIONS} onPick={send} />}

      {messages.map((message) => (
        <LibraryMessage key={message.id} message={message} onOpen={open} />
      ))}
      {isSending && <ThinkingIndicator label="本棚の本から探して考えています…" />}
      <div ref={endRef} />
    </div>
  )
}

const LibraryMessage = ({
  message,
  onOpen,
}: {
  message: LibraryChatMessage
  onOpen: (citation: LibraryCitation) => void
}) => {
  if (message.role === 'user') return <UserBubble content={message.content} />
  return (
    <AssistantBubble
      content={message.content}
      citations={
        message.citations.length > 0 &&
        message.citations.map((citation) => (
          <CitationChip
            key={citation.chunk_id}
            citation={citation}
            label={`${citation.book_title}・${citationLabel(citation)}`}
            onClick={() => onOpen(citation)}
          />
        ))
      }
    >
      {message.passages.length > 0 && <FoundPassages passages={message.passages} onOpen={onOpen} />}
    </AssistantBubble>
  )
}

/** 質問で見つかった本文（検索結果） */
const FoundPassages = ({
  passages,
  onOpen,
}: {
  passages: LibraryCitation[]
  onOpen: (citation: LibraryCitation) => void
}) => {
  const [open, setOpen] = useState(false)
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border-t pt-2">
      <CollapsibleTrigger className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground">
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        <Library className="size-3.5" />
        見つかった箇所（{passages.length}）
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-1.5 space-y-1.5">
          {passages.map((passage) => (
            <li key={passage.chunk_id}>
              <button
                type="button"
                onClick={() => onOpen(passage)}
                className="w-full space-y-0.5 rounded-md bg-background px-2 py-1.5 text-left hover:bg-accent/40"
              >
                <span className="line-clamp-2 block text-xs wrap-break-word">{passage.excerpt}</span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {passage.book_title}・{citationLabel(passage)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}
